import Cookies from 'universal-cookie';
import { CookieUtils } from 'Shared/cookies/cookie-utils';
import { isConsentRequiredCountryCode } from '@ifixit/utils';
import { SentryError } from '@ifixit/sentry';

declare const App: { apiUrlParams?: Record<string, string> };

interface GeoLocation {
   countryCode: string | undefined;
   regionCode: string | undefined;
}

const cookies = new Cookies();

let locationPromise: Promise<GeoLocation> | null = null;

export const Geo = {
   locate(): Promise<GeoLocation> {
      if (locationPromise) {
         return locationPromise;
      }

      locationPromise = new Promise<GeoLocation>((resolve, reject) => {
         function done(countryCode?: string, regionCode?: string) {
            resolve({ countryCode, regionCode });
         }

         // Allow easy and simple override for debugging.
         const countryMatch = window.location.search.match(/countryCode=(\w+)/);
         const regionMatch = window.location.search.match(/regionCode=(\w+)/);

         if (countryMatch) {
            CookieUtils.set('countryCodeOverride', countryMatch[1], {
               maxAgeInSeconds: 3600, // 1 hour
            });
         }

         if (countryMatch || regionMatch) {
            if (countryMatch && regionMatch) {
               done(countryMatch[1], regionMatch[1]);
               return;
            } else if (countryMatch) {
               done(countryMatch[1]);
               return;
            } else {
               done(undefined, regionMatch![1]);
               return;
            }
         }

         const countryCodeOverride = cookies.get('countryCodeOverride');
         if (countryCodeOverride) {
            done(countryCodeOverride);
            return;
         }

         const countryCode = cookies.get('countryCode');
         const regionCode = cookies.get('regionCode');

         if (countryCode != null && regionCode != null) {
            done(countryCode, regionCode);
            return;
         }

         const url = new URL('/api/2.0/geo_locate', window.location.origin);
         if (App.apiUrlParams) {
            for (const [key, value] of Object.entries(App.apiUrlParams)) {
               url.searchParams.set(key, String(value));
            }
         }

         fetch(url.toString(), { credentials: 'include' })
            .then(response => {
               if (!response.ok) {
                  throw new SentryError(`geo_locate failed: ${response.status}`);
               }
               return response.json();
            })
            .then(data => {
               CookieUtils.set('countryCode', data.country_code, {
                  maxAgeInSeconds: 432_000, // 5 days
               });
               CookieUtils.set('regionCode', data.region_code, {
                  maxAgeInSeconds: 432_000, // 5 days
               });

               done(data.country_code, data.region_code);
            })
            .catch(reject);
      });

      return locationPromise;
   },

   runWhereConsentNotRequired(callback: () => void): void {
      Geo.locate().then(
         ({ countryCode }) => {
            if (countryCode && !isConsentRequiredCountryCode(countryCode)) {
               callback();
            }
         },
         error => console.error('Geolocation lookup failed', error)
      );
   },
};
